import * as React from 'react';
import { useFormik } from 'formik';
import { MessageMask } from 'types';
import { useSelector, useDispatch } from 'react-redux';
import { Radio, Row, Col, Input, RadioChangeEvent } from 'antd';
import styled from 'styled-components';

import { useIntl } from 'common/config/i18n';
import { StyledRepositoryFormButtonWrapper } from 'common/styling/mixins';
import Button from 'components/molecules/button';
import { MessagesMasksActions, MessagesMasksSelectors } from 'common/config/store';
import history from 'app-router/history';

const FormWrapper = styled.div`
  width: 100%%;
  padding-left: 20px;
  padding-right: 20px;
  margin-top: 10px;
`;

const UpLabel = styled.div<{ withMarginBottom?: boolean }>`
  font-size: 16px;
  font-weight: bold;
  ${({ withMarginBottom }) => withMarginBottom && 'margin-bottom: 10px;'}
`;

const SubLabel = styled.div`
  font-size: 14px;
  font-weight: bold;
`;

const RadioGroupWrraper = styled(Col)`
  margin-right: 30px;
  margin-left: 30px;
  margin-top: 5px;
  margin-bottom: 5px;
`;

const RadioGroupRow = styled(Row)`
  justify-content: space-between;
  align-items: center;
`;

interface Props {
  constitution?: MessageMask;
  isUpdate?: boolean;
}

const RadioCard = ({
  title,
  subLabel,
  withInput,
  radioValue,
  onChangeRadioValue,
  inputValue,
  onChangeInputValue,
}: {
  title?: string;
  subLabel?: string;
  withInput?: boolean;
  radioValue: boolean;
  onChangeRadioValue: (e: RadioChangeEvent) => void;
  inputValue?: string;
  onChangeInputValue?: React.ChangeEventHandler<HTMLInputElement>;
  children?: React.ReactNode;
}) => {
  const { t } = useIntl();

  return (
    <Col style={{ padding: 8 }}>
      {!!title && <UpLabel withMarginBottom={!!subLabel}>{title}</UpLabel>}
      <Radio.Group onChange={onChangeRadioValue} value={radioValue} style={{ width: '100%' }}>
        <RadioGroupWrraper>
          {!!subLabel && <SubLabel>{subLabel}</SubLabel>}
          <RadioGroupRow>
            <Col>
              <Radio value style={{ fontSize: 16 }}>
                {t('form', 'YES')}
              </Radio>
            </Col>
            {withInput && (
              <Col>
                <Input onChange={onChangeInputValue} value={inputValue} />
              </Col>
            )}
            <Col>
              <Radio value={false} style={{ fontSize: 16 }}>
                {t('form', 'NO')}
              </Radio>
            </Col>
          </RadioGroupRow>
        </RadioGroupWrraper>
      </Radio.Group>
    </Col>
  );
};

const Form = (props: Props) => {
  const { t } = useIntl();
  const dispatch = useDispatch();

  const status = useSelector(MessagesMasksSelectors.statusSelector);

  const initialValues = {
    id: props?.constitution?.id,
    hasReason: props?.constitution?.hasReason,
    hasVolumeDelay: props?.constitution?.hasVolumeDelay,
    hasSubstituteStation: props?.constitution?.hasSubstituteStation,
    hasDock: props?.constitution?.hasDock,
    hasPrincipalZoneLongMessage: props?.constitution?.hasPrincipalZoneLongMessage,
    hasPrincipalZoneShortMessage: props?.constitution?.hasPrincipalZoneShortMessage,
    hasSecondaryZoneLongMessage: props?.constitution?.hasSecondaryZoneLongMessage,
    hasSecondaryZoneShortMessage: props?.constitution?.hasSecondaryZoneShortMessage,
    hasCompensationZoneForAll: props?.constitution?.hasCompensationZoneForAll,
    hasCompensationZoneForUp: props?.constitution?.hasCompensationZoneForUp,
    hasCompensationZoneForDown: props?.constitution?.hasCompensationZoneForDown,
    hasCompensationZoneForUpOrDown: props?.constitution?.hasCompensationZoneForUpOrDown,
    hasCompensationZoneForSpecificBookings: props?.constitution?.hasCompensationZoneForSpecificBookings,
    hasSigningZone: props?.constitution?.hasSigningZone,
    signingContent: props?.constitution?.signingContent,
  };
  const formik = useFormik({
    initialValues,
    onSubmit: (values) => {
      dispatch(MessagesMasksActions.updateMessageMask(values, () => history.push('/constitution')));
    },
  });

  return (
    <form onSubmit={formik.handleSubmit}>
      <FormWrapper>
        <RadioCard
          title={t('form', 'REASON')}
          radioValue={!!formik.values.hasReason}
          onChangeRadioValue={(e) => formik.setFieldValue('hasReason', e.target.value)}
        />

        <RadioCard
          title={t('form', 'DELAY')}
          radioValue={!!formik.values.hasVolumeDelay}
          onChangeRadioValue={(e) => formik.setFieldValue('hasVolumeDelay', e.target.value)}
        />

        <RadioCard
          title={t('form', 'SUBSTITUTE')}
          radioValue={!!formik.values.hasSubstituteStation}
          onChangeRadioValue={(e) => formik.setFieldValue('hasSubstituteStation', e.target.value)}
        />

        <RadioCard
          title={t('form', 'DOCK')}
          radioValue={!!formik.values.hasDock}
          onChangeRadioValue={(e) => formik.setFieldValue('hasDock', e.target.value)}
        />

        <RadioCard
          title={t('form', 'COMM_INFO')}
          subLabel={t('form', 'COMM_INFO_LONG')}
          radioValue={!!formik.values.hasPrincipalZoneLongMessage}
          onChangeRadioValue={(e) => formik.setFieldValue('hasPrincipalZoneLongMessage', e.target.value)}
        />

        <RadioCard
          subLabel={t('form', 'COMM_INFO_SHORT')}
          radioValue={!!formik.values.hasPrincipalZoneShortMessage}
          onChangeRadioValue={(e) => formik.setFieldValue('hasPrincipalZoneShortMessage', e.target.value)}
        />

        <RadioCard
          title={t('form', 'MORE_INFO')}
          subLabel={t('form', 'COMM_INFO_LONG')}
          radioValue={!!formik.values.hasSecondaryZoneLongMessage}
          onChangeRadioValue={(e) => formik.setFieldValue('hasSecondaryZoneLongMessage', e.target.value)}
        />

        <RadioCard
          subLabel={t('form', 'COMM_INFO_SHORT')}
          radioValue={!!formik.values.hasSecondaryZoneShortMessage}
          onChangeRadioValue={(e) => formik.setFieldValue('hasSecondaryZoneShortMessage', e.target.value)}
        />

        <RadioCard
          title={t('form', 'COMPENSATION')}
          subLabel={t('form', 'COMPENSATION_ALL')}
          radioValue={!!formik.values.hasCompensationZoneForAll}
          onChangeRadioValue={(e) => formik.setFieldValue('hasCompensationZoneForAll', e.target.value)}
        />

        <RadioCard
          subLabel={t('form', 'COMPENSATION_UP')}
          radioValue={!!formik.values.hasCompensationZoneForUp}
          onChangeRadioValue={(e) => formik.setFieldValue('hasCompensationZoneForUp', e.target.value)}
        />

        <RadioCard
          subLabel={t('form', 'COMPENSATION_DOWN')}
          radioValue={!!formik.values.hasCompensationZoneForDown}
          onChangeRadioValue={(e) => formik.setFieldValue('hasCompensationZoneForDown', e.target.value)}
        />

        <RadioCard
          subLabel={t('form', 'COMPENSATION_UP_DOWN')}
          radioValue={!!formik.values.hasCompensationZoneForUpOrDown}
          onChangeRadioValue={(e) => formik.setFieldValue('hasCompensationZoneForUpOrDown', e.target.value)}
        />

        <RadioCard
          subLabel={t('form', 'COMPENSATION_DV_NUMBERS')}
          radioValue={!!formik.values.hasCompensationZoneForSpecificBookings}
          onChangeRadioValue={(e) => formik.setFieldValue('hasCompensationZoneForSpecificBookings', e.target.value)}
        />

        <RadioCard
          title={t('form', 'SIGN')}
          withInput
          radioValue={!!formik.values.hasSigningZone}
          onChangeRadioValue={(e) => {
            formik.setFieldValue('hasSigningZone', e.target.value);
            if (e.target.value === false) formik.setFieldValue('signingContent', '');
          }}
          inputValue={formik.values.signingContent}
          onChangeInputValue={(e) => formik.setFieldValue('signingContent', e.target.value)}
        />

        <StyledRepositoryFormButtonWrapper>
          <Button height="35px" width="100%" borderRadius="20px" htmlType="submit" loading={status === 'loading'}>
            {t('Buttons', 'SAVE_BUTTON')}
          </Button>
        </StyledRepositoryFormButtonWrapper>
      </FormWrapper>
    </form>
  );
};
export default Form;
